<script setup lang="ts">
 
/**
 * Site navigation shell: owns store/route/i18n, responsive layout (breakpoint + overflow probe),
 * mega menu vs burger, search panels, drawer scroll lock, and global dismiss listeners.
 * Presentational pieces live under `components/navigation/`; tokens live on `.side-nav` in side-nav.css.
 */
import { computed, ref, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Menu, X } from 'lucide-vue-next';
import { useI18n } from 'vue-i18n';
import { stripLocalePrefix } from '@/i18n';
import { useUiDisplayLocale } from '@/composables/useUiDisplayLocale';
import { useContentStore } from '@/stores/content';
import {
  normalizePath,
  mapTopMenuLinksToView,
  buildTopMenuChildrenByKey,
  type TopMenuLinkView,
} from '@/nav/menuModel';
import { useNavBreakpoints } from '@/composables/useNavBreakpoints';
import { useMegaMenu } from '@/composables/useMegaMenu';
import { useDesktopNavOverflow } from '@/composables/useDesktopNavOverflow';
import NavSiteBrand from '@/components/navigation/NavSiteBrand.vue';
import NavToolbarTools from '@/components/navigation/NavToolbarTools.vue';
import NavSearchField from '@/components/navigation/NavSearchField.vue';
import DesktopNavLinks from '@/components/navigation/DesktopNavLinks.vue';
import DesktopMegaMenu from '@/components/navigation/DesktopMegaMenu.vue';
import DesktopBarProbe from '@/components/navigation/DesktopBarProbe.vue';
import NavDrawerPanel from '@/components/navigation/NavDrawerPanel.vue';
import { defaultNavSiteBrandProps } from '@/components/navigation/navSiteBrandDefaults';

const route = useRoute();
const router = useRouter();
const contentStore = useContentStore();
const { t } = useI18n();

const drawerOpen = ref(false);
const isWideScreen = ref(true);

const desktopNavDrawerOpen = ref(false);
const desktopBarInnerRef = ref<HTMLElement | null>(null);
const desktopBarProbeCmp = ref<InstanceType<typeof DesktopBarProbe> | null>(null);
const desktopRowProbeEl = computed(() => desktopBarProbeCmp.value?.rootRef ?? null);

const currentLocale = useUiDisplayLocale();
const resolvedNavigation = computed(() =>
  contentStore.getResolvedNavigation(currentLocale.value)
);
const topNavData = computed(() => resolvedNavigation.value.topNavData);
const siteName = computed(
  () => topNavData.value?.siteName?.trim() || defaultNavSiteBrandProps.siteName
);
const siteSubtitle = computed(
  () => topNavData.value?.siteSubtitle?.trim() || defaultNavSiteBrandProps.siteSubtitle
);
const siteLogos = computed(() => topNavData.value?.siteLogos ?? defaultNavSiteBrandProps.siteLogos);

const resolvedTopMenu = computed(() => resolvedNavigation.value.topMenuLinks);

const topMenuLinks = computed(() => mapTopMenuLinksToView(resolvedTopMenu.value));

const topMenuChildrenByKey = computed(() => buildTopMenuChildrenByKey(resolvedTopMenu.value));

const currentPath = computed(() => normalizePath(route.path));
const expandedTopSectionKeys = ref<Set<string>>(new Set());

function isActive(url: string): boolean {
  return currentPath.value === normalizePath(url);
}

function topMenuChildren(item: TopMenuLinkView): Array<{ path: string; label: string }> {
  return topMenuChildrenByKey.value.get(item.sectionKey) ?? [];
}

function hasActiveTopMenuChild(item: TopMenuLinkView): boolean {
  return topMenuChildren(item).some((child) => isActive(child.path));
}

function hasTopMenuChildren(item: TopMenuLinkView): boolean {
  return topMenuChildren(item).length > 0;
}

function isTopMenuExpanded(item: TopMenuLinkView): boolean {
  if (expandedTopSectionKeys.value.has(item.sectionKey)) {
    return true;
  }
  return isActive(item.path) || hasActiveTopMenuChild(item);
}

function toggleTopMenuChildren(item: TopMenuLinkView): void {
  const next = new Set(expandedTopSectionKeys.value);
  if (next.has(item.sectionKey)) {
    next.delete(item.sectionKey);
  } else {
    next.add(item.sectionKey);
  }
  expandedTopSectionKeys.value = next;
}

const { desktopNeedsBurger, checkDesktopOverflow, setupDesktopLayoutObservers } =
  useDesktopNavOverflow(
    isWideScreen,
    desktopBarInnerRef,
    desktopRowProbeEl,
    () => [isWideScreen.value, topMenuLinks.value.length, contentStore.status] as const
  );

const isSearchOpen = ref(false);
const headerSearchPanelRef = ref<HTMLElement | null>(null);
const mobileSearchPanelRef = ref<HTMLElement | null>(null);
const mobileSearchFieldRef = ref<InstanceType<typeof NavSearchField> | null>(null);
const headerSearchFieldRef = ref<InstanceType<typeof NavSearchField> | null>(null);

const mobileToolbarRef = ref<InstanceType<typeof NavToolbarTools> | null>(null);
const desktopToolbarRef = ref<InstanceType<typeof NavToolbarTools> | null>(null);

const desktopNavRootRef = ref<HTMLElement | null>(null);

/** HorizontalNav: scrollY > 10 → bg-white/95 backdrop-blur-md shadow-md. */
const isHeaderScrolled = ref(false);

function updateHeaderScroll(): void {
  isHeaderScrolled.value = window.scrollY > 10;
}

const useDesktopMegaMenu = computed(
  () => isWideScreen.value && !desktopNeedsBurger.value
);

const {
  activeDesktopSectionKey,
  megaMenuClosingSoon,
  clearMegaCloseTimer,
  closeDesktopDropdown,
  onDesktopNavItemEnter,
  onDesktopNavItemLeave,
  onMegaPanelEnter,
  onMegaPanelLeave,
  onMegaPanelFocusIn,
  onDesktopTriggerFocus,
} = useMegaMenu(useDesktopMegaMenu, isSearchOpen);

const activeMegaItem = computed(() => {
  const key = activeDesktopSectionKey.value;
  if (!key) return null;
  const item = topMenuLinks.value.find((i) => i.sectionKey === key);
  return item && hasTopMenuChildren(item) ? item : null;
});

useNavBreakpoints(isWideScreen, (wide) => {
  if (wide) {
    drawerOpen.value = false;
  } else {
    desktopNeedsBurger.value = false;
    desktopNavDrawerOpen.value = false;
    closeDesktopDropdown();
  }
});

const panelDrawerOpen = computed(
  () =>
    (!isWideScreen.value && drawerOpen.value) ||
    (isWideScreen.value && desktopNeedsBurger.value && desktopNavDrawerOpen.value)
);

function closeDesktopNavDrawer(): void {
  desktopNavDrawerOpen.value = false;
}

function toggleDesktopNavDrawer(): void {
  desktopNavDrawerOpen.value = !desktopNavDrawerOpen.value;
}

function closeSearch(): void {
  isSearchOpen.value = false;
}

function toggleSearch(): void {
  isSearchOpen.value = !isSearchOpen.value;
  if (isSearchOpen.value) {
    closeDesktopDropdown();
  }
}

function onNavSearchSelect(payload: { url: string }): void {
  void router.push(payload.url);
  closeSearch();
}

function isSearchUiTarget(target: Node): boolean {
  return (
    (mobileToolbarRef.value?.searchBtnRef?.contains(target) ?? false) ||
    (headerSearchPanelRef.value?.contains(target) ?? false) ||
    (desktopToolbarRef.value?.searchBtnRef?.contains(target) ?? false) ||
    (mobileSearchPanelRef.value?.contains(target) ?? false)
  );
}

function onDesktopNavClick(): void {
  closeDesktopDropdown();
}

function openDrawer(): void {
  drawerOpen.value = true;
}
function closeDrawer(): void {
  drawerOpen.value = false;
}
function onNavClick(): void {
  if (!isWideScreen.value) closeDrawer();
  else if (desktopNeedsBurger.value) closeDesktopNavDrawer();
}

const previousPathForDrawer = ref(route.path);

watch(
  () => route.path,
  (newPath) => {
    const prev = previousPathForDrawer.value;
    previousPathForDrawer.value = newPath;
    const localeOnly =
      stripLocalePrefix(newPath) === stripLocalePrefix(prev) && newPath !== prev;
    if (!localeOnly) {
      if (!isWideScreen.value) closeDrawer();
      closeDesktopNavDrawer();
    }
    closeDesktopDropdown();
    closeSearch();
  }
);

watch(isSearchOpen, (open) => {
  if (!open) return;
  nextTick(() => {
    const el = isWideScreen.value
      ? headerSearchFieldRef.value?.inputRef
      : mobileSearchFieldRef.value?.inputRef;
    el?.focus({ preventScroll: true });
  });
});

watch(isWideScreen, () => {
  closeSearch();
});

watch([drawerOpen, desktopNavDrawerOpen], ([mobileOpen, desktopBurgerOpen]) => {
  if (mobileOpen || desktopBurgerOpen) closeSearch();
});

watch(
  [drawerOpen, isWideScreen, desktopNavDrawerOpen, desktopNeedsBurger],
  () => {
    const mobileLock = drawerOpen.value && !isWideScreen.value;
    const desktopLock =
      isWideScreen.value && desktopNeedsBurger.value && desktopNavDrawerOpen.value;
    document.body.style.overflow = mobileLock || desktopLock ? 'hidden' : '';
  },
  { immediate: true }
);

watch(desktopNeedsBurger, (needs) => {
  if (!needs) closeDesktopNavDrawer();
  if (needs) closeDesktopDropdown();
});

let removeDocListeners: (() => void) | undefined;

onMounted(() => {
  function handlePointerDown(e: MouseEvent): void {
    const target = e.target as Node;
    if (desktopNavRootRef.value && !desktopNavRootRef.value.contains(target)) {
      closeDesktopDropdown();
    }
    if (isSearchOpen.value && !isSearchUiTarget(target)) {
      closeSearch();
    }
  }

  function handleKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      closeDesktopDropdown();
      if (isSearchOpen.value) {
        closeSearch();
        return;
      }
      if (!isWideScreen.value) closeDrawer();
      else closeDesktopNavDrawer();
    }
  }

  function handleWindowResize(): void {
    requestAnimationFrame(checkDesktopOverflow);
  }

  document.addEventListener('mousedown', handlePointerDown);
  document.addEventListener('keydown', handleKeydown);
  window.addEventListener('resize', handleWindowResize);
  window.addEventListener('scroll', updateHeaderScroll, { passive: true });
  updateHeaderScroll();
  removeDocListeners = () => {
    document.removeEventListener('mousedown', handlePointerDown);
    document.removeEventListener('keydown', handleKeydown);
    window.removeEventListener('resize', handleWindowResize);
    window.removeEventListener('scroll', updateHeaderScroll);
  };

  nextTick(() => {
    checkDesktopOverflow();
    setupDesktopLayoutObservers();
  });
});
onUnmounted(() => {
  clearMegaCloseTimer();
  removeDocListeners?.();
  document.body.style.overflow = '';
});
</script>

<template>
  <div class="side-nav" :class="{ 'side-nav--drawer-open': panelDrawerOpen }">
    <!-- Collapsed top bar (≤1023px) -->
    <div
      v-if="!isWideScreen"
      class="side-nav__mobile-header-wrap"
      :class="{ 'side-nav__mobile-header-wrap--scrolled': isHeaderScrolled }"
    >
      <header
        class="side-nav__top-bar"
        role="banner"
      >
        <NavSiteBrand
          :root-url="topNavData?.siteRootUrl ?? defaultNavSiteBrandProps.rootUrl"
          :site-name="siteName"
          :site-subtitle="siteSubtitle"
          :site-logos="siteLogos"
          link-class="side-nav__desktop-brand--in-top-bar"
          @click="onNavClick"
        />
        <NavToolbarTools
          ref="mobileToolbarRef"
          variant="mobile-bar"
          :search-open="isSearchOpen"
          :search-label="t('nav.search')"
          :login-label="t('nav.login')"
          @toggle-search="toggleSearch"
          @login-click="onNavClick"
        >
          <template #trailing>
            <button
              type="button"
              class="side-nav__menu-btn side-nav__top-bar-menu-btn"
              :aria-label="drawerOpen ? 'Close menu' : 'Open menu'"
              :aria-expanded="drawerOpen"
              @click="drawerOpen ? closeDrawer() : openDrawer()"
            >
              <X v-if="drawerOpen" class="side-nav__icon side-nav__icon--menu-toggle" aria-hidden="true" />
              <Menu v-else class="side-nav__icon side-nav__icon--menu-toggle" aria-hidden="true" />
            </button>
          </template>
        </NavToolbarTools>
      </header>
      <div
        v-show="isSearchOpen"
        id="side-nav-mobile-search"
        ref="mobileSearchPanelRef"
        class="side-nav__mobile-search-panel"
      >
        <NavSearchField
          ref="mobileSearchFieldRef"
          :placeholder="t('nav.search')"
          :search-open="isSearchOpen"
          :current-locale="currentLocale"
          @select="onNavSearchSelect"
        />
      </div>
    </div>

    <!-- Desktop top navigation (≥1024px) -->
    <header
      v-if="isWideScreen"
      ref="desktopNavRootRef"
      class="side-nav__desktop-bar"
      :class="{ 'side-nav__desktop-bar--scrolled': isHeaderScrolled }"
      role="banner"
    >
      <div ref="desktopBarInnerRef" class="side-nav__desktop-bar-inner">
        <NavSiteBrand
          :root-url="topNavData?.siteRootUrl ?? defaultNavSiteBrandProps.rootUrl"
          :site-name="siteName"
          :site-subtitle="siteSubtitle"
          :site-logos="siteLogos"
          @click="onDesktopNavClick"
        />

        <DesktopNavLinks
          v-show="!desktopNeedsBurger"
          :top-menu-links="topMenuLinks"
          :is-search-open="isSearchOpen"
          :use-desktop-mega-menu="useDesktopMegaMenu"
          :active-desktop-section-key="activeDesktopSectionKey"
          :has-top-menu-children="hasTopMenuChildren"
          :has-active-top-menu-child="hasActiveTopMenuChild"
          :is-active="isActive"
          @nav-item-enter="onDesktopNavItemEnter"
          @nav-item-leave="onDesktopNavItemLeave"
          @trigger-focus="onDesktopTriggerFocus"
          @nav-click="onDesktopNavClick"
        />

        <NavToolbarTools
          ref="desktopToolbarRef"
          variant="desktop"
          :search-open="isSearchOpen"
          :search-label="t('nav.search')"
          :login-label="t('nav.login')"
          :show-desktop-burger="desktopNeedsBurger"
          :desktop-nav-drawer-open="desktopNavDrawerOpen"
          @toggle-search="toggleSearch"
          @login-click="onDesktopNavClick"
          @toggle-desktop-burger="
            desktopNavDrawerOpen ? closeDesktopNavDrawer() : toggleDesktopNavDrawer()
          "
        />
      </div>

      <div
        v-show="isSearchOpen"
        id="side-nav-desktop-search"
        ref="headerSearchPanelRef"
        class="side-nav__desktop-search-panel"
      >
        <NavSearchField
          ref="headerSearchFieldRef"
          :placeholder="t('nav.search')"
          :search-open="isSearchOpen"
          :current-locale="currentLocale"
          @select="onNavSearchSelect"
        />
      </div>

      <Transition name="side-nav-desktop-mega">
        <DesktopMegaMenu
          v-if="activeMegaItem && useDesktopMegaMenu && !isSearchOpen"
          :active-mega-item="activeMegaItem"
          :children="topMenuChildren(activeMegaItem)"
          :mega-intro="t('sideNav.megaSectionIntro')"
          :is-active="isActive"
          :closing-soon="megaMenuClosingSoon"
          @mega-enter="onMegaPanelEnter"
          @mega-leave="onMegaPanelLeave"
          @focus-in="onMegaPanelFocusIn"
          @nav-click="onDesktopNavClick"
        />
      </Transition>
    </header>

    <DesktopBarProbe
      v-if="isWideScreen"
      ref="desktopBarProbeCmp"
      :site-name="siteName"
      :site-subtitle="siteSubtitle"
      :site-logos="siteLogos"
      :root-url="topNavData?.siteRootUrl ?? defaultNavSiteBrandProps.rootUrl"
      :top-menu-links="topMenuLinks"
      :has-top-menu-children="hasTopMenuChildren"
    />

    <div
      v-if="!isWideScreen || desktopNeedsBurger"
      class="side-nav__backdrop"
      :class="{ 'side-nav__backdrop--open': panelDrawerOpen }"
      aria-hidden="true"
      @click="!isWideScreen ? closeDrawer() : closeDesktopNavDrawer()"
    />

    <NavDrawerPanel
      v-if="!isWideScreen || desktopNeedsBurger"
      :top-menu-links="topMenuLinks"
      :is-wide-screen-desktop-drawer="isWideScreen && desktopNeedsBurger"
      :has-top-menu-children="hasTopMenuChildren"
      :top-menu-children="topMenuChildren"
      :has-active-top-menu-child="hasActiveTopMenuChild"
      :is-top-menu-expanded="isTopMenuExpanded"
      :is-active="isActive"
      :login-label="t('nav.login')"
      :login-account-label="t('nav.loginAccount')"
      @toggle-children="toggleTopMenuChildren"
      @nav-click="onNavClick"
    />
  </div>
</template>

<style>
@import './navigation/side-nav.css';
</style>
